import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useMatch } from 'react-router-dom';

import applicationVersion from '../../../../application_version.txt';
import type { RootState } from '../../store/store';
import classNames from '../../utils/classNames';
import { getModuleHref, MODULES } from '../../utils/mainModules';
import { ROUTES } from '../../utils/routes';
import UserMenu from './components/UserMenu/UserMenu.module';

const logo = require('../../../img/logo.svg');

type ModuleType = typeof MODULES[0];

type HeaderModuleType = ModuleType & {
  isActive: boolean;
};

const Header: React.FC = () => {
  const location = useLocation();
  const isHomeRoute = useMatch(ROUTES.HOME);
  const isBackOffice = useSelector((state: RootState) => state.auth.isBackOffice);
  const [hoveredModule, setHoveredModule] = useState<HeaderModuleType | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const currentModule = MODULES.find(module => {
    const normalizedLocation = location.pathname.replace(/^\//, '');
    const normalizedItemHref = module.route.replace(/^\//, '');   
    return normalizedLocation.startsWith(normalizedItemHref);
  });

  const dashboardModule: ModuleType = {
    title: 'Tableau de bord',
    route: ROUTES.HOME,
    displayDashboard: false,
    displayHeader: true,
    items: [],
    img: null,
    subtitle: '',
    freemium: true,
    moduleTypeId: null,
  };

  const headerModules: ModuleType[] = [
    dashboardModule,
    ...MODULES.filter(module => module.displayHeader || currentModule?.route === module.route || (isBackOffice && module.route.includes('/admin'))),
  ];

  const isAnyDashboard = Boolean(isHomeRoute);

  const moduleMatches: HeaderModuleType[] = headerModules.map(item => ({
    ...item,
    isActive: item === currentModule || (isAnyDashboard && item.route === ROUTES.HOME),
  }));
  
  const commonClassesExternal = 'text-gray-500 hover:text-gray-800 px-1 py-2 border-b-2 border-transparent hover:border-gray-800 transition-all duration-200';
  const mainNavClasses = 'text-[14px] font-medium text-gray-500 py-[10px] px-5 border-b-2 border-transparent hover:text-gray-800 transition-all duration-200';
  const subNavClasses = 'text-[14px] font-medium rounded-full px-5 py-2 transition-all duration-200 text-center';
  const subNavInactiveClasses = 'text-gray-500 hover:text-gray-800 hover:bg-gray-100';
  const subNavActiveClasses = 'bg-rgpd-blue font-semibold text-white shadow-sm hover:bg-rgpd-blue hover:text-white focus:bg-rgpd-blue focus:text-white';

  return (
    <nav className={classNames(
      'bg-white border-b border-gray-200 sticky top-0 z-[999]',
      isBackOffice && 'lg:ml-64',
    )}>
      {/* Main Header Bar */}
      <div className="flex w-full items-center justify-between px-4 py-4 md:px-6 lg:justify-normal">
        {/* Mobile: Left hamburger + Logo container for mobile */}
        <div className="flex items-center lg:flex-1">
          <button
            type="button"
            className="mr-4 inline-flex items-center justify-center rounded-xl p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="block h-6 w-6" />
            ) : (
              <Bars3Icon className="block h-6 w-6" />
            )}
          </button>

          {/* Logo - centered on mobile, left-aligned on desktop */}
          <div className="absolute left-1/2 -translate-x-1/2 lg:static lg:left-auto lg:!translate-x-[0px]">
            <Link to={ROUTES.EXTERNAL_CONFORMITE} className="relative flex items-center">
              <img src={logo} alt="Logo" className="h-10 md:h-12" />
              <span className="absolute bottom-2 left-3 translate-y-[80%] text-[9px] text-gray-300">
                {applicationVersion}
              </span>
            </Link>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-8 text-[15px] font-medium text-gray-500 lg:flex">
          <a href={ROUTES.EXTERNAL_CONFORMITE} className={commonClassesExternal}>Conformité</a>
          <Link to={ROUTES.HOME} className={`${commonClassesExternal} !border-rgpd-blue-dark font-semibold !text-rgpd-blue-dark`}>Croissance</Link>
        </div>

        {/* User Menu - Simplified icon version on mobile */}
        <div className="flex items-center lg:flex-1 lg:justify-end">
          <UserMenu />
        </div>
      </div>

      {/* Mobile Menu Drawer - Slides from left */}
      <div 
        className={`fixed inset-0 z-[10000] transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Overlay */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity ${
            isMobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Drawer content */}
        <div className="relative flex h-full w-4/5 max-w-sm flex-col bg-white shadow-xl">
          <div className="border-b border-gray-200 p-4">
            {isMobileMenuOpen && <UserMenu hideOnMobile={false} />}
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Main Navigation Links */}
            <div className="space-y-1 px-2 py-3">
              <a
                href={ROUTES.EXTERNAL_CONFORMITE}
                className="block rounded-xl px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              >
                Conformité
              </a>
              <Link
                to={ROUTES.HOME}
                className="block rounded-xl bg-rgpd-blue px-3 py-2 text-base font-medium text-white hover:bg-rgpd-blue-dark"
              >
                Croissance
              </Link>
            </div>

            {/* Module Navigation */}
            <div className="border-t border-gray-200 px-2 py-3">
              {moduleMatches.map(item => (
                <div key={item.title}>
                  <Link
                    to={getModuleHref(item)}
                    className={`block rounded-xl px-3 py-2 text-base font-medium ${
                      item.isActive ? 'bg-gray-50 text-rgpd-blue' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                  {item.items && item.items.length > 0 && item.isActive && (
                    <div className="space-y-1 pl-6">
                      {item.items.map(subItem => (
                        <Link
                          key={subItem.title}
                          to={subItem.href}
                          className={`block rounded-xl px-3 py-2 text-sm font-medium ${
                            location.pathname.startsWith(subItem.href)
                              ? 'bg-gray-50 text-rgpd-blue'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Module Navigation - Hidden when in back office (sidebar is used instead) */}
      <div 
        onMouseLeave={() => setHoveredModule(null)}
        className={classNames('hidden lg:flex flex-col', isBackOffice && 'lg:hidden')}
      >
        {/* Main Navigation */}
        <div className="border-t border-gray-200">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-0 px-4 py-0">
            {moduleMatches.map(item => (
              <div
                key={item.title}
                onMouseEnter={() => setHoveredModule(item)}
                className="flex items-center"
              >
                <Link
                  to={getModuleHref(item)}
                  className={
                    classNames(
                      mainNavClasses,
                      !isBackOffice && ((item.isActive && !hoveredModule) || (hoveredModule && item.route === hoveredModule.route)) 
                      && '!text-gray-800 !font-semibold !border-b-2 !border-gray-800',
                      isBackOffice && ((item.isActive && !hoveredModule) || (hoveredModule && item.route === hoveredModule.route)) 
                      && 'hover:text-white hover:bg-slate-500 font-bold bg-slate-500',
                      isBackOffice && ((item.isActive && !hoveredModule) || (hoveredModule && item.route === hoveredModule.route)) 
                      && 'text-white',
                    )
                  }
                >
                  {item.title}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Sub Navigation */}
        {((hoveredModule || (!isHomeRoute && currentModule))?.items || []).length > 0 && (
          <div className={classNames('border-t border-gray-100 bg-white py-3', isBackOffice ? 'bg-slate-500' : '')}>
            <div className="flex flex-wrap items-center justify-center gap-2 px-4">
              {(hoveredModule || currentModule)?.items.map(item => (
                <Link
                  key={item.title}
                  to={item.href}
                  className={classNames(
                    subNavClasses,
                    location.pathname.startsWith(item.href) && !hoveredModule ? subNavActiveClasses : subNavInactiveClasses,
                  )}
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header;
